// =====================================================================
//  The pack opening simulator: a fun way to build a random deck.
//
//  A deck built from packs starts with the Emperor Pack (10 cards and an Emperor), then any five more
//  packs (repeats allowed) for 60 cards. Each card in a pack is rolled on its own: first its rarity, from
//  the pack's rarity chances, then its card type, from the pack's type chances, then a card of that rarity
//  and type at random. If no card of that rarity and type exists, a pack of only certain types (Spells &
//  Support, Reinforcements, Siege) takes the nearest rarity of that type; the others take another type of
//  that rarity. A card the deck can't take another copy of is quietly swapped for a different card.
//
//  Everything about the packs is in PACKS below: chances are in percent per card.
// =====================================================================
import { RARITIES, isEmperor, type CardDb, type CardDef } from './cards.ts';
import { whyCantAdd, type Deck } from './deck.ts';
import { leaningsOf } from './archetypes.ts';
import { CONFIG } from '../config.ts';

export type Rarity = (typeof RARITIES)[number];
/** Chances by rarity, in percent per card. A group ("rare/ultra-rare": 20) is shared equally between its rarities. */
type Odds = Record<string, number>;

export interface PackDef {
  id: string;
  name: string;
  blurb: string;
  colors: [string, string]; // the foil's two colors
  // Pack art: an image in public/packs/ drawn as the whole pack instead of the colored foil. Leave it out
  // and the game looks for public/packs/<id>.png; with no image there, the foil is drawn. Best at 5:8
  // (500 x 800, say): the top 9% is what tears off as the pack is opened.
  art?: string;
  set?: string; // the set its cards come from (left out: 'base', the Base Set). New sets get packs of their own
  rarity: Odds;
  types: Odds; // chances by card type, in percent per card: troop, spell, support (Biomes, Tomes, Triggers), building, biome, tome, trigger
  // What happens when no card of the rolled rarity and type exists (every Biome is Rare, say). true: keep the type
  // and take the nearest rarity. false: keep the rarity and take another type. Left out: true for a pack with no Troops.
  keepType?: boolean;
  // Cards that lean toward any of these deck archetypes (the ids in ARCHETYPE_IDS, src/shared/cards.ts) are
  // LEAN_WEIGHT times as likely to be the card picked. The other cards can still turn up.
  leans?: string[];
  // At most this many cards of a type and rarity in one pack ("only 1 Ultra-Rare Tome"): any more of that type come at its other rarities.
  caps?: { type: string; rarity: string; max: number }[];
  note?: string; // a line for the pack's page, under its chances
  emperor?: boolean; // an Emperor comes at the very end (the Emperor Pack)
  ancients?: { guaranteed: number; extraChance: number }; // the Ancient Pack: Ancients on top of the rolled cards
}

export const PACK_SIZE = 10;
/** How much likelier a card that leans a pack's way (its `leans`) is than one that doesn't. */
export const LEAN_WEIGHT = 3;
/** A deck from packs: the Emperor Pack, then this many more. */
export const PACKS_AFTER_EMPEROR = 5;

// the first five packs pull Troops most, then Spells, then Support cards and Buildings equally
const MAIN_TYPES: Odds = { troop: 55, spell: 25, support: 10, building: 10 };
const GLISTER: Odds = { 'rare/ultra-rare': 25, epic: 30, 'mythic/legendary': 30, grand: 10, ancient: 5 };
const COMMON: Odds = { common: 50, uncommon: 20, 'rare/ultra-rare': 20, 'epic/mythic': 10 };

export const PACKS: PackDef[] = [
  {
    id: 'emperor', name: 'Emperor Pack', blurb: 'Every deck starts here: 10 cards and your Emperor.', colors: ['#d6a443', '#6b3f0e'], emperor: true,
    // 20% C, 20% UC, 15% R, 25% UR/E/M/L, 15% more for C/UC/R, 5% G/Ancient
    rarity: { common: 20, uncommon: 20, rare: 15, 'ultra-rare/epic/mythic/legendary': 25, 'common/uncommon/rare': 15, 'grand/ancient': 5 },
    types: MAIN_TYPES,
  },
  { id: 'common', name: 'Common Pack', blurb: 'Plenty of everyday cards to build around.', colors: ['#8f9aa6', '#39424c'], rarity: COMMON, types: MAIN_TYPES },
  {
    id: 'boost', name: 'Boost Pack', blurb: 'A step up: Uncommons and Rares, with a real shot at Legendary and Grand.', colors: ['#3fa36b', '#16402a'],
    rarity: { common: 10, 'uncommon/rare': 40, 'ultra-rare/epic/mythic': 20, 'legendary/grand': 27.5, ancient: 2.5 }, types: MAIN_TYPES,
  },
  { id: 'glister', name: 'Glister Pack', blurb: 'No Commons at all: Epic and up most of the time.', colors: ['#4fa3e0', '#173a63'], rarity: GLISTER, types: MAIN_TYPES },
  {
    id: 'uber', name: 'Uber Pack', blurb: 'Glister, turned up: more Mythics, Legendaries and Ancients.', colors: ['#b05ce0', '#3c1659'],
    rarity: { 'rare/ultra-rare': 15, epic: 30, 'mythic/legendary': 37.5, grand: 10, ancient: 7.5 }, types: MAIN_TYPES,
  },
  {
    id: 'ancient', name: 'Ancient Pack', blurb: 'Mythic, Legendary and Grand only, with an Ancient guaranteed.', colors: ['#e05c3a', '#4a140a'],
    rarity: { 'mythic/legendary/grand': 100 }, types: MAIN_TYPES, ancients: { guaranteed: 1, extraChance: 10 },
  },
  {
    id: 'spells', name: 'Spells & Support Pack', blurb: 'Common Pack chances, but only Spells (about 2 in 3) and Support cards.', colors: ['#3ac0b0', '#0f4842'],
    rarity: COMMON, types: { spell: 66.7, support: 33.3 },
  },
  {
    id: 'reinforcements', name: 'Reinforcements Pack', blurb: 'Glister Pack chances, Spells (about 2 in 3) and Support cards only.', colors: ['#e0c13a', '#4d3f08'],
    rarity: GLISTER, types: { spell: 66.7, support: 33.3 },
  },
  {
    id: 'siege', name: 'Siege Pack', blurb: 'Glister Pack chances: three quarters Buildings, the rest Spells and Biomes.', colors: ['#9a7350', '#36261a'],
    rarity: GLISTER, types: { building: 75, spell: 12.5, biome: 12.5 },
  },
  {
    id: 'explorer', name: 'Explorer’s Pack', blurb: 'Chart new ground: Biomes most of all, with Spells and Troops for the journey.', colors: ['#5fb04a', '#1f4a2e'],
    rarity: { common: 10, uncommon: 10, rare: 10, 'ultra-rare': 20, epic: 20, mythic: 10, legendary: 10, grand: 7.5, ancient: 2.5 },
    types: { troop: 25, biome: 40, spell: 35 }, keepType: true,
    note: 'Almost every Biome is Rare, so a Biome nearly always comes as a Rare whatever rarity was rolled.',
  },
  {
    id: 'arcane', name: 'Arcane Knowledge Pack', blurb: 'Tomes, Spells and Triggers for the studious, with a few Biomes. Nothing below Rare.', colors: ['#7a5ce0', '#241552'],
    rarity: { rare: 20, 'ultra-rare': 25, epic: 15, mythic: 10, legendary: 10, grand: 10, ancient: 10 },
    types: { biome: 10, trigger: 20, spell: 30, tome: 40 },
    caps: [{ type: 'tome', rarity: 'ultra-rare', max: 1 }],
    note: 'Only one Ultra-Rare Tome per pack: any more Tomes are Epic or Mythic. Biomes are nearly always Rare and Triggers always Ultra-Rare.',
  },
  {
    id: 'conquest', name: 'Conquest Pack', blurb: 'Buildings and Spells that hit hard and fast, with the odd Trigger: for decks on the attack.', colors: ['#e0503a', '#571410'],
    rarity: { common: 10, uncommon: 10, rare: 10, 'ultra-rare': 20, epic: 10, mythic: 10, legendary: 20, grand: 5, ancient: 5 },
    types: { building: 50, spell: 40, trigger: 10 },
    leans: ['aggro', 'tempo', 'beatdown', 'swarm', 'direct_attack', 'voltron', 'brainrot'],
    note: 'Cards that suit aggressive decks turn up more often. Triggers are always Ultra-Rare.',
  },
  {
    id: 'infrastructure', name: 'Infrastructure Pack', blurb: 'Buildings, Spells and Biomes to build a plan on: for decks that play the long game.', colors: ['#5c8fa8', '#17303f'],
    rarity: { common: 10, uncommon: 10, rare: 10, 'ultra-rare': 20, epic: 10, mythic: 10, legendary: 20, grand: 5, ancient: 5 },
    types: { building: 40, spell: 30, biome: 30 },
    leans: ['combo', 'support', 'control', 'mastermind', 'ramp', 'stall', 'synergy', 'toolbox', 'prison', 'token', 'typecast'],
    note: 'Cards that suit combo, support and long-game decks turn up more often. Biomes are nearly always Rare.',
  },
];
export const packById = (id: string) => PACKS.find((p) => p.id === id);
/**
 * How the pack menu is laid out: one line here is one row of packs on the shelf, top to bottom, by pack id.
 * Move an id to another line to regroup it. A pack left out of every line is shown in a last row of its own.
 */
export const PACK_ROWS: string[][] = [
  ['emperor'],
  ['common', 'boost', 'glister', 'uber', 'ancient'],
  ['spells', 'reinforcements', 'explorer', 'arcane'],
  ['siege', 'conquest', 'infrastructure'],
];
/** The packs, row by row, as the pack menu shows them. */
export function packRows(): PackDef[][] {
  const rows = PACK_ROWS.map((ids) => ids.map(packById).filter((p): p is PackDef => !!p));
  const placed = new Set(PACK_ROWS.flat());
  const rest = PACKS.filter((p) => !placed.has(p.id));
  return [...rows, rest].filter((r) => r.length);
}
/** Where a pack's art is looked for (inside public/). */
export const packArtPath = (p: PackDef) => p.art ?? `packs/${p.id}.png`;

const SUPPORT = ['biome', 'tome', 'trigger'];
const typeKind = (c: CardDef, kind: string) => (kind === 'support' ? c.types.some((t) => SUPPORT.includes(t)) : c.types.includes(kind));

/** Each rarity's chance in percent, with the groups shared out ("rare/ultra-rare": 20 → 10 each). */
export function rarityChances(pack: PackDef): Map<string, number> {
  const out = new Map<string, number>();
  for (const [group, pct] of Object.entries(pack.rarity)) {
    const list = group.split('/');
    for (const r of list) out.set(r, (out.get(r) ?? 0) + pct / list.length);
  }
  return new Map(RARITIES.filter((r) => out.has(r)).map((r) => [r, out.get(r)!]));
}

function roll(odds: Map<string, number> | [string, number][], rng: () => number): string {
  const list = [...odds];
  const total = list.reduce((n, [, p]) => n + p, 0);
  let x = rng() * total;
  for (const [k, p] of list) {
    x -= p;
    if (x < 0) return k;
  }
  return list[list.length - 1][0];
}

export interface PulledCard {
  card: CardDef;
}
export interface OpenedPack {
  pack: PackDef;
  // a secret rare was in this pack: the rest of its set (CONFIG.secretRares.autofill) plus one more card from the pack
  // go into the deck by themselves, and they use up one of the deck's pack openings
  bonus: CardDef[];
  cards: CardDef[]; // in the order they come out: least rare at the front, most rare at the back (the Emperor last)
}

/**
 * Opens a pack: `deck` is the deck so far (each card is checked against it, and against the cards already pulled
 * from this pack, so the deck never goes over a card's copy limit). Returns null if the card list can't fill one.
 */
export function openPack(packId: string, db: CardDb, deck: Deck, rng: () => number = Math.random, opts: { noSecrets?: boolean } = {}): OpenedPack | null {
  const pack = packById(packId);
  if (!pack) return null;
  const working: Deck = new Map(deck);
  const inSet = (c: CardDef) => (c.set ?? 'base') === (pack.set ?? 'base'); // (a pack only ever holds cards of its own set)
  const pool = db.list.filter((c) => inSet(c) && !isEmperor(c) && !c.spawnOnly && !c.types.includes('unassigned') && RARITIES.includes(c.rarity as Rarity));
  const rarities = rarityChances(pack);
  const types = Object.entries(pack.types);
  const cards: CardDef[] = [];
  // a card fits if the deck can take another copy and the pack isn't at its cap for that type and rarity
  const capped = (c: CardDef) => (pack.caps ?? []).some((cap) => c.rarity === cap.rarity && c.types.includes(cap.type) && cards.filter((x) => x.rarity === cap.rarity && x.types.includes(cap.type)).length >= cap.max);
  // The three secret rares (The Remaining, Realm Beyond Comprehension, Calling Pillar) only work together, so pulling
  // any one of them fills the deck with the whole set (CONFIG.secretRares.autofill: 1 The Remaining, 2 Realms and
  // 7 Pillars in all, counting the one pulled), and that uses up one of the deck's pack openings. So one can only be
  // pulled when the deck has room for the whole set, and never from the last packs of a deck (opts.noSecrets). Once
  // one is in this pack, the others aren't pulled as well: they come with the set.
  const fill = Object.entries(CONFIG.secretRares.autofill).map(([tag, copies]) => ({ card: db.list.find((c) => c.whileInPlay.includes(tag)), copies }));
  const isSecret = (c: CardDef) => fill.some((f) => f.card === c);
  const roomForAll = () => fill.every((f) => !!f.card && Math.max(working.get(f.card.number) ?? 0, f.copies) <= f.card.limit);
  const called = () => cards.some(isSecret);
  const secretOk = (c: CardDef) => !isSecret(c) || (!opts.noSecrets && !called() && roomForAll());
  const fits = (c: CardDef) => !whyCantAdd(working, c, db) && !capped(c) && secretOk(c);
  // one of these at random: a card leaning the pack's way counts LEAN_WEIGHT times
  const leansIn = (c: CardDef) => !!pack.leans?.length && [...leaningsOf(c).keys()].some((id) => pack.leans!.includes(id));
  const pick = (list: CardDef[]) => {
    if (!pack.leans?.length) return list[Math.floor(rng() * list.length)];
    const weights = list.map((c) => (leansIn(c) ? LEAN_WEIGHT : 1));
    let x = rng() * weights.reduce((n, w) => n + w, 0);
    for (let i = 0; i < list.length; i++) {
      x -= weights[i];
      if (x < 0) return list[i];
    }
    return list[list.length - 1];
  };

  // a pack of only certain kinds (Spells & Support, Siege…) keeps the kind and moves the rarity when it has to;
  // the others keep the rarity and take another kind of card (there is no Grand Biome, say)
  const strict = pack.keepType ?? !('troop' in pack.types);
  /**
   * A card of this rarity and kind; failing that (strict packs) the nearest rarity of the same kind, or (the
   * others) another kind of the same rarity; failing that anything that fits.
   */
  const draw = (rarity: string, kind: string): CardDef | null => {
    if (!strict) {
      const exact = pool.filter((c) => c.rarity === rarity && typeKind(c, kind) && fits(c));
      if (exact.length) return pick(exact);
      const sameRarity = pool.filter((c) => c.rarity === rarity && fits(c));
      if (sameRarity.length) return pick(sameRarity);
    }
    const at = RARITIES.indexOf(rarity as Rarity);
    const order = [...RARITIES].sort((a, b) => Math.abs(RARITIES.indexOf(a) - at) - Math.abs(RARITIES.indexOf(b) - at) || RARITIES.indexOf(a) - RARITIES.indexOf(b));
    for (const r of order) {
      const some = pool.filter((c) => c.rarity === r && typeKind(c, kind) && fits(c));
      if (some.length) return pick(some);
    }
    // the deck can't take any more of that kind: another card of the same rarity, then any card at all
    const same = pool.filter((c) => c.rarity === rarity && fits(c));
    if (same.length) return pick(same);
    const any = pool.filter(fits);
    return any.length ? pick(any) : null;
  };

  const add = (c: CardDef | null) => {
    if (!c) return false;
    working.set(c.number, (working.get(c.number) ?? 0) + 1);
    cards.push(c);
    return true;
  };
  const ancients = pack.ancients ? pack.ancients.guaranteed + (rng() * 100 < pack.ancients.extraChance ? 1 : 0) : 0;
  for (let i = 0; i < PACK_SIZE - ancients; i++) if (!add(draw(roll(rarities, rng), roll(types, rng)))) return null;
  for (let i = 0; i < ancients; i++) if (!add(draw('ancient', roll(types, rng)))) return null;

  // least rare at the front, most rare at the back…
  const rank = (c: CardDef) => RARITIES.indexOf(c.rarity as Rarity);
  cards.sort((a, b) => rank(a) - rank(b));
  // …but a Rare, Ultra-Rare or Epic now and then jumps forward, for a surprise early on
  const mid = ['rare', 'ultra-rare', 'epic'];
  for (let i = 3; i < cards.length; i++) {
    if (mid.includes(cards[i].rarity) && rng() < 0.3) {
      const [c] = cards.splice(i, 1);
      cards.splice(Math.floor(rng() * 3), 0, c);
    }
  }
  if (pack.emperor) {
    const emperors = db.list.filter((c) => isEmperor(c) && inSet(c) && fits(c));
    if (emperors.length) cards.push(pick(emperors));
  }
  // the rest of the set (whatever the deck and this pack don't already hold), then one more card from this pack, so
  // the pack plus the set's opening still comes to exactly two packs' worth of cards
  const bonus: CardDef[] = [];
  if (called()) {
    for (const f of fill) {
      if (!f.card) continue;
      const have = working.get(f.card.number) ?? 0;
      for (let i = have; i < f.copies; i++) bonus.push(f.card);
      working.set(f.card.number, Math.max(have, f.copies));
    }
    const short = PACK_SIZE - bonus.length;
    for (let i = 0; i < short; i++) {
      const extra = draw(roll(rarities, rng), roll(types, rng));
      if (!extra) break;
      working.set(extra.number, (working.get(extra.number) ?? 0) + 1);
      bonus.push(extra);
    }
  }
  return { pack, cards, bonus };
}

/** For a pack's page: its chances, as lines to show ("Common · 25%"). */
export function packOddsText(pack: PackDef) {
  const rare = [...rarityChances(pack)].map(([r, p]) => ({ label: r, pct: p }));
  const kinds: Record<string, string> = { troop: 'Troops', spell: 'Spells', support: 'Support (Biomes, Tomes, Triggers)', building: 'Buildings', biome: 'Biomes', tome: 'Tomes', trigger: 'Triggers' };
  const type = Object.entries(pack.types).map(([k, p]) => ({ label: kinds[k] ?? k, pct: p }));
  return { rarity: rare, type };
}
